import axios, { AxiosRequestConfig } from 'axios';
import { ENVS, isSessionExpiredError } from '../../../http';
import { store } from '../../../store';

const env = process.env.REACT_APP_ENV || 'dev';

// Support Agent management-plane endpoints live under `/v1/support-agent/*`
// on pom-2-api (feature/support-agent-admin, design doc §6.7/§6.8) — same
// base URL as the shared v1 client. A dedicated axios instance (mirroring
// the v2-settings-api.ts pattern) keeps this file self-contained and easy
// to mock in tests without reaching into the shared `api` singleton.
const baseURL = ENVS?.[env]?.BASE_URL ?? 'http://localhost:3333/v1';

export const supportAgentHttp = axios.create({ baseURL });

// Mirror the shared client's request interceptor — attach the auth token
// from the redux store on every request. Without this every request fails
// with MISSING_TOKEN.
supportAgentHttp.interceptors.request.use((config: AxiosRequestConfig) => {
  const headers: Record<string, string> = {
    authorization: `${store.getState().auth.token}`,
  };
  if (env === 'ngrok') {
    headers['ngrok-skip-browser-warning'] = 'true';
  }
  return { ...config, headers } as AxiosRequestConfig;
});

// Mirror the shared client's response interceptor — surface 401 / 403 as
// the same window events the rest of the app already listens to.
supportAgentHttp.interceptors.response.use(
  (r) => r,
  (error: any) => {
    if (isSessionExpiredError(error)) {
      window.dispatchEvent(new Event('sessionExpired'));
    }
    if (error?.response?.status === 403) {
      window.dispatchEvent(new Event('permissionsChanged'));
    }
    return Promise.reject(error);
  },
);

// ── Shapes (mirrors pom-2-api src/types/support-agent.types.ts +
//    src/controllers/support-agent.controller.ts response envelopes,
//    feature/support-agent-admin) ─────────────────────────────────────────

// Every route responds with the repo's PresenterFactory envelope:
// `{ result, message?, isValid }`.
interface Envelope<T> {
  result: T;
  message?: string;
  isValid: boolean;
}

export type KbDocStatus = 'draft' | 'published' | 'archived';
// The create/update validators (support-agent.validations.ts `kbStatus`)
// only accept draft|published — archiving is its own DELETE endpoint, not a
// status value you can PUT.
export type KbDocWritableStatus = Extract<KbDocStatus, 'draft' | 'published'>;

// Which product an article is about (pom-2-api support-agent.types.ts
// KbAudience). The server omits it on untagged docs — absent means 'both'.
// The support agent keeps 'safety_hub' articles out of mobile-app chats.
export type KbAudience = 'app' | 'safety_hub' | 'both';

// NOTE: the doc-detail response's version-history entries do NOT include
// `body` (see SupportAgentKbService.toVersionSummary in pom-2-api) — only
// title, tags, status, updatedBy, updatedAt. The full snapshot (incl. body)
// for a given version is a separate lookup — GET /kb/:id/versions/:index,
// added in feature/support-agent-admin@153d465 — see `getKbDocVersion`
// below and `KbDocVersionSnapshot`.
export interface KbDocVersionSummary {
  title: string;
  status: KbDocStatus;
  audience?: KbAudience;
  tags: string[];
  updatedBy: string;
  updatedAt: string;
}

// Full stored snapshot for one version, returned by
// GET /kb/:id/versions/:index (index 0 = most recent prior version —
// versions are stored most-recent-first, kb.service.ts `getVersionByIndex`).
export interface KbDocVersionSnapshot {
  title: string;
  body: string;
  tags: string[];
  status: KbDocStatus;
  audience?: KbAudience;
  updatedBy: string;
  updatedAt: string;
}

// Same fields as the persisted snapshot, plus the `index` the caller asked
// for, echoed back by the server for convenience (pom-2-api
// feature/support-agent-observe@8cd3b3a) — e.g. so the version-history
// drawer's "View" dialog can correlate a response with the card that
// triggered it. Only ever a response payload — nothing persists `index`.
export interface KbDocVersionDetail extends KbDocVersionSnapshot {
  index: number;
}

export interface KbDocListItem {
  id: string;
  title: string;
  tags: string[];
  status: KbDocStatus;
  audience?: KbAudience;
  updatedBy: string;
  updatedAt: string;
  chunkCount: number;
}

export interface KbDocListResult {
  items: KbDocListItem[];
  total: number;
  page: number;
  limit: number;
}

// Provenance (KB conflict check, Planner 9/19) — write-only in v1: stored when
// the editor supplies it, echoed on the detail, read by nothing yet. Mirrors
// pom-2-api support-agent.types.ts KbDocSource.
export type KbDocSourceType = 'zendesk' | 'code' | 'manual';
export interface KbDocSource {
  type: KbDocSourceType;
  ref?: string;
  commit?: string;
}

export interface KbDocDetail {
  id: string;
  title: string;
  tags: string[];
  body: string;
  status: KbDocStatus;
  audience?: KbAudience;
  sourceFileKey?: string;
  source?: KbDocSource | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  versions: KbDocVersionSummary[];
}

// Mirrors kbUpload.service.ts's PENDING_EXTRACTION_BODY distinguishing
// prefix — an uploaded doc whose text hasn't been extracted yet starts
// with this. NOTE: only checkable where `body` is present — that's
// `KbDocDetail` (the editor), NOT `KbDocListItem` (the list), which omits
// `body` entirely. See knowledge-base-panel.tsx for where this is shown.
export const KB_PENDING_EXTRACTION_BODY_PREFIX = '*(File uploaded';

export function isPendingExtraction(body: string | undefined | null): boolean {
  return !!body && body.startsWith(KB_PENDING_EXTRACTION_BODY_PREFIX);
}

export interface ListKbDocsParams {
  q?: string;
  page?: number;
  limit?: number;
}

export interface CreateKbDocInput {
  title: string;
  tags?: string[];
  body: string;
  status?: KbDocWritableStatus;
  // Absent = 'both'.
  audience?: KbAudience;
  source?: KbDocSource;
}

export interface UpdateKbDocInput {
  title?: string;
  tags?: string[];
  body?: string;
  status?: KbDocWritableStatus;
  // Absent = keep. There is no "clear": an untagged doc behaves as 'both'.
  audience?: KbAudience;
  // Absent = keep; null = clear; object = replace.
  source?: KbDocSource | null;
}

// ── KB conflict check (Michael, 9/19) ────────────────────────────────────
//
// Mirrors the pomsafe-services document (src/lib/kbConflictCheck/store.ts +
// findings.ts) as pom-2-api's proxy returns it: ObjectIds as hex strings,
// Dates as ISO strings. Only the fields the drawer renders are typed
// strictly; the rest is passthrough.

export type KbConflictCheckState = 'queued' | 'running' | 'completed' | 'partial' | 'failed';
export type KbConflictRelation = 'potential_conflict' | 'missing_qualification' | 'compatible' | 'uncertain';
export type KbConflictConsequence = 'safety' | 'operational' | 'editorial';
export type KbConflictDispositionAction = 'fix_draft' | 'propose_edit' | 'dismiss' | 'accept_risk';

export interface KbConflictFindingDisposition {
  action: KbConflictDispositionAction;
  reason?: string;
  by: string;
  at: string;
}

export interface KbConflictFinding {
  id: string;
  kind: 'cross' | 'internal';
  relation: KbConflictRelation;
  consequence: KbConflictConsequence;
  draft: { unitIndex: number; quote: string; offsetStart: number | null; offsetEnd: number | null };
  existing:
    | {
        kind: 'chunk';
        chunkId: string;
        docId: string;
        docTitle: string;
        heading: string;
        quote: string;
        offsetStart: number | null;
        offsetEnd: number | null;
        foundBy: Array<'vector' | 'bm25'>;
      }
    | { kind: 'unit'; unitIndex: number; sectionHeading: string; quote: string; offsetStart: number | null; offsetEnd: number | null };
  conditions: { draft: string[]; existing: string[] };
  why: string;
  quoteValid: boolean;
  quoteProblems?: Array<'draftQuote' | 'existingQuote'>;
  repaired?: boolean;
  disposition?: KbConflictFindingDisposition;
}

/**
 * GET /kb/conflict-checks/:checkId — the stored document itself, serialized by
 * the Lambda (ObjectIds as hex), so its key is Mongo's `_id`. Deliberately NOT
 * the same as KbConflictCheckSummary's `id`: that list is shaped by pom-2-api's
 * toSummary(). Each interface mirrors its own endpoint; "aligning" the two
 * would break whichever one changed.
 */
export interface KbConflictCheck {
  _id: string;
  state: KbConflictCheckState;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  requestedBy?: string;
  snapshot: { docId: string | null; title: string; bodyHash: string; body: string };
  corpus?: { publishedDocs: number; chunkCount: number; pendingVectors: number; embeddingModel: string };
  coverage?: {
    units: number;
    vectorStatus: 'ok' | 'failed' | 'skipped' | 'pending-chunks';
    backfilledVectors: number;
    deadlineHit: boolean;
    vectorQueriesOk: number;
    lexicalQueriesOk: number;
    candidateChunks: number;
    candidateArticles: number;
  };
  judge?: {
    model: string;
    promptVersion: string;
    status: 'ok' | 'skipped' | 'partial';
    unitsWithCandidates: number;
    unitsJudged: number;
    unitsFailed: number;
    unitsSkippedDeadline: number;
    unitsCandidatesGone?: number;
    candidatesGone?: number;
    repairCalls: number;
    repairsFailed?: number;
    invalidQuotes: number;
    internal: 'ok' | 'failed' | 'skipped-too-long' | 'skipped' | 'deadline';
  };
  usage?: { inputTokens: number; outputTokens: number; costUsd: number | null; judgeCalls: number };
  findings?: KbConflictFinding[];
}

/** GET /kb/:id/conflict-checks — shaped by pom-2-api's toSummary(): `id`, not `_id` (see KbConflictCheck). */
export interface KbConflictCheckSummary {
  id: string;
  state: KbConflictCheckState;
  createdAt: string;
  finishedAt?: string;
  error?: string;
  requestedBy?: string;
  title: string;
  bodyHash: string;
  findingCount: number;
  openFindingCount: number;
  judgeStatus?: string;
  vectorStatus?: string;
}

export interface CreateKbConflictCheckInput {
  docId?: string;
  title: string;
  body: string;
}

export interface CreateKbConflictCheckResult {
  checkId: string;
  state: 'queued';
}

export interface SetKbConflictFindingDispositionInput {
  action: KbConflictDispositionAction;
  reason?: string;
}

export const KB_CONFLICT_TERMINAL_STATES: ReadonlySet<KbConflictCheckState> = new Set<KbConflictCheckState>(['completed', 'partial', 'failed']);

export interface InstructionsVersionSummary {
  text: string;
  updatedBy: string;
  updatedAt: string;
}

export interface InstructionsResult {
  text: string;
  updatedBy: string | null;
  updatedAt: string | null;
  versions: InstructionsVersionSummary[];
}

export type DraftVerdict = 'up' | 'down' | null;

/**
 * Which drafting mode produced a given ticket record (pom-2-api
 * feature/support-agent-observe, 8/12 rework). 'observe' = activity
 * recorded, no draft ever ran (same records as the pre-existing `observed`
 * flag). 'shadow' = a draft was generated for review but never posted to
 * the ticket — the new default. 'live' = the draft was posted as a private
 * note. Absent on records predating this field — never default it in the
 * TYPE, but see `resolveMode` in activity-panel.tsx for how the UI infers
 * one for legacy records.
 */
export type SupportAgentTicketMode = 'observe' | 'shadow' | 'live';

/**
 * Pre-draft triage classification (pom-2-api sales/spam-skip feature,
 * 8/13) plus the filtered-senders skip (Carlos, 8/18). 'sales_or_vendor' and
 * 'spam_or_automated' mean the triage classified the incoming message as
 * sales/vendor outreach or automated spam and the worker deliberately
 * produced no draft at all (zero cost, nothing sent) — see
 * `skipped`/`skipReason` on `ActivityListItem` / `SupportAgentTicketRecord`
 * below. 'ignored_sender' is DISTINCT from those two: it means the sender is
 * on the admin-managed filtered-senders list (Settings tab), not that the
 * triage judged the message itself as spam — the UI must render this as
 * "Filtered · sender" (see activity-panel.tsx's SkippedChip), never lumped
 * in with the sales/spam "Skipped" label. Absent on records predating each
 * respective field.
 */
export type SupportAgentTicketCategory = 'support' | 'sales_or_vendor' | 'spam_or_automated' | 'ignored_sender';

// A chat turn's `category` comes from the same dry-run classifier as a
// regular ticket's, but a chat turn can additionally land in one of three
// values a ticket never gets (Interrogator finding, 2026-09-11 round 2 —
// pom-2-api's chat.service.ts assigns this field straight from the
// classifier's SupportAgentTicketRecord['category'], a 7-value union, with
// no server-side narrowing): `no_customer_message`, `superseded`, and
// `rate_limited` are chat-only outcomes. Kept as a separate type rather than
// widening SupportAgentTicketCategory itself, since nothing indicates a
// regular ticket can ever actually take these three values.
export type SupportAgentChatTurnCategory =
  | SupportAgentTicketCategory
  | 'no_customer_message'
  | 'superseded'
  | 'rate_limited';

/**
 * ONE ROW PER TICKET (pom-2-api 8/12 rework) — the latest record's fields
 * plus `eventCount`/`lastActivityAt` aggregated across every drafting pass
 * for that ticket. The full per-message timeline lives behind
 * `getActivityRecords` (the drawer's records endpoint) — see
 * `SupportAgentTicketRecord` below.
 */
export interface ActivityListItem {
  // Mongo _id, hex string, of the LATEST record for this ticket — usable as
  // a default verdict target, but the grid itself is keyed on
  // `zendeskTicketId` now that it's one row per ticket (see activity-panel.tsx
  // `getRowId`).
  recordId: string;
  zendeskTicketId: number;
  topic?: string;
  confidence?: number;
  urgent?: boolean;
  // Why the bot flagged this one urgent — only meaningful when `urgent` is
  // true. Shown under the Urgent chip in both the grid row and the drawer
  // timeline.
  urgencyReason?: string;
  costUsd?: number;
  verdict: DraftVerdict;
  createdAt: string;
  draftSnippet: string;
  // Set when this record's note was replayed by the worker's crash-recovery
  // path rather than a normal drafting run — no analysis ran, so topic,
  // confidence, urgencyReason, and cited*Ids are all intentionally absent
  // and costUsd is 0. Optional/absent on records written before this field
  // existed. Passed through by pom-2-api's activity list/detail endpoints
  // (parallel fix, feature/support-agent-admin).
  recovered?: boolean;
  // Set (true) only on records written by the worker's observe-only mode —
  // ticket activity recorded WITHOUT drafting (no draftReply, no urgency
  // screen, no citations; costUsd 0). Absent on every record predating
  // observe-only mode (pom-2-api feature/support-agent-observe). Superseded
  // by `mode` below going forward, but still passed through for legacy rows.
  observed?: boolean;
  // See SupportAgentTicketMode above — pass-through of the latest record's
  // mode. Absent on records predating this field.
  mode?: SupportAgentTicketMode;
  // Human-readable ticket subject — the most legible identifier for a row,
  // especially an observed one with no draft/topic to show instead.
  // Optional/absent on legacy rows.
  subject?: string;
  // Short preview of the latest customer message, only meaningful on
  // observed records (there's no draft to summarize the ticket instead).
  latestMessagePreview?: string;
  // See SupportAgentTicketCategory above — pass-through of the latest
  // record's triage classification. Absent on records predating this field.
  category?: SupportAgentTicketCategory;
  // True when the pre-draft triage classified this message as sales/vendor
  // outreach or automated spam and the bot deliberately produced no draft
  // (zero cost, nothing sent) — the ticket itself is untouched in Zendesk.
  // Absent on records predating this field.
  skipped?: boolean;
  // Present when `skipped` is true — the triage's stated reason, shown
  // under the Skipped chip.
  skipReason?: string;
  // Total supportAgentTickets records for this ticket, across every
  // drafting pass — what `getActivityRecords` returns in full.
  eventCount: number;
  // `createdAt` of the latest record — shown in the grid as "Last activity".
  lastActivityAt: string;
  // Per-ticket posting enrollment (Carlos, 8/17, pom-2-api
  // feature/support-agent-posting, parallel/pinned). When true, every future
  // draft on THIS ticket posts to Zendesk as a private note regardless of
  // the global agent mode (see SupportAgentGlobalMode below) — set via
  // `updatePosting`. Absent/false on tickets never enrolled.
  postingEnabled?: boolean;
}

export interface ActivityListResult {
  items: ActivityListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface SupportAgentTicketRecord {
  // Present on the detail/verdict responses (ActivityDetail on the server)
  // — the specific append-only record this response is about. Send it back
  // on submitVerdict so the verdict lands on THIS record, not just "the
  // latest for the ticket" (feature/support-agent-admin@bd9e77c).
  recordId: string;
  zendeskTicketId: number;
  eventId: string;
  createdAt: string;
  updatedAt: string;
  topic?: string;
  confidence?: number;
  urgent?: boolean;
  // Why the bot flagged this one urgent — only meaningful when `urgent` is
  // true.
  urgencyReason?: string;
  draftReply?: string;
  citedDocIds?: string[];
  citedChunkIds?: string[];
  costUsd?: number;
  verdict: DraftVerdict;
  verdictBy?: string;
  verdictAt?: string;
  verdictNote?: string;
  // See ActivityListItem.recovered — same meaning, on the detail record.
  recovered?: boolean;
  // See ActivityListItem.observed/subject/latestMessagePreview — same
  // meaning, on the detail record.
  observed?: boolean;
  // See SupportAgentTicketMode / ActivityListItem.mode above.
  mode?: SupportAgentTicketMode;
  subject?: string;
  latestMessagePreview?: string;
  // See ActivityListItem.category/skipped/skipReason above — same meaning,
  // on this record specifically.
  category?: SupportAgentTicketCategory;
  skipped?: boolean;
  skipReason?: string;
}

// ── Activity thread (full Zendesk conversation) ────────────────────────
// GET /activity/:zendeskTicketId/thread — pom-2-api thread.service.ts
// (feature/support-agent-observe, PR #457). Proxies the support-agent
// Lambda's own thread endpoint. Oldest-first. 404s as
// SUPPORT_AGENT_TICKET_NOT_FOUND (ticket gone upstream); 503s as
// SUPPORT_AGENT_THREAD_UNAVAILABLE when the upstream Lambda/Zendesk fetch
// itself fails — both are shown to the user as a single generic "unavailable"
// message rather than surfacing the raw error code (see activity-panel.tsx).
export interface ActivityThreadComment {
  id: string;
  authorId: string;
  authorName: string;
  // True for the AI's own drafts sitting in the thread as private notes —
  // NOT a real Zendesk agent/customer. Bodies are markdown (rendered via
  // MDEditor.Markdown); every other comment's body is plain text.
  isBot: boolean;
  // Zendesk's public/private distinction — false means an internal note,
  // visible only to agents.
  public: boolean;
  body: string;
  createdAt: string;
}

export interface ActivityThreadResult {
  subject: string;
  status: string;
  // True when the upstream Lambda capped the comment list (currently at
  // 100, most recent) rather than returning the full history.
  truncated: boolean;
  comments: ActivityThreadComment[];
}

// ── Per-ticket posting toggle (Carlos, 8/17) ──────────────────────────────
// PUT /activity/:zendeskTicketId/posting { enabled } — pom-2-api
// feature/support-agent-posting (verify against that branch before relying
// on this — parallel/pinned as of this writing). Enrolling a ticket makes
// every future draft on it post to Zendesk as a private note regardless of
// the global mode (see SupportAgentGlobalMode below); it does NOT change
// what happens on other tickets.
export interface PostingImmediatePostResult {
  posted: boolean;
  // Only meaningful when `posted` is false — why the enable call didn't post
  // anything right away. 'no_draft': the ticket has no draft yet. 'already_posted':
  // the current draft was already on the ticket (idempotent enable).
  // 'error': the immediate post attempt itself failed — the toggle still
  // enrolled the ticket, so the NEXT draft still posts automatically.
  // 'ticket_closed': added to the Lambda after this UI was first written —
  // Zendesk auto-closes solved tickets and the Lambda rejects posting a note
  // to a closed ticket, so the toggle still enrolls the ticket (the same as
  // 'error') but nothing can post until the ticket reopens.
  reason?: 'no_draft' | 'already_posted' | 'error' | 'ticket_closed';
}

export interface UpdatePostingResult {
  zendeskTicketId: number;
  enabled: boolean;
  // Present only on an enable call (never on disable) — the outcome of
  // trying to post the ticket's current draft immediately.
  immediatePost?: PostingImmediatePostResult;
}

// ── Global agent mode (Carlos, 8/17 Settings tab) ─────────────────────────
// GET/PUT /settings/mode — pom-2-api feature/support-agent-posting (same
// parallel/pinned branch as the posting toggle above). `null` means no
// explicit setting has ever been saved — the worker falls back to its own
// deployment-default mode, which this UI shows as "Shadow-equivalent" (see
// settings-panel.tsx) since shadow is the deployment default in practice.
export type SupportAgentGlobalMode = 'observe' | 'shadow' | 'live';

export interface SupportAgentModeResult {
  mode: SupportAgentGlobalMode | null;
  // Present only when `mode` is non-null — who last changed it and when.
  updatedBy?: string;
  updatedAt?: string;
}

// ── Idle-chat auto-resolve (Carlos, 10/29 Settings tab) ───────────────────
// GET/PUT /settings/chat-auto-resolve — how many hours an AI chat may sit idle
// before the sweep resolves it. Stored per environment (dev/QA/prod each have
// their own value). `isDefault` is true while nothing has been saved: the API
// then reports its default of 12 hours. Allowed range 1–720 (1 hour–30 days).
export interface SupportAgentChatAutoResolveResult {
  hours: number;
  isDefault: boolean;
  // Present only once a value has been saved.
  updatedBy?: string;
  updatedAt?: string;
}

// ── Ignored senders list (Carlos, 8/18 Settings tab) ──────────────────────
// GET/PUT /settings/ignored-senders — pom-2-api feature/support-agent-posting
// (same parallel/pinned branch as the mode setting above). Messages from
// senders on this list are skipped entirely by the worker: recorded in
// Activity as skipped ("ignored sender") and never drafted or posted, in ANY
// mode — including on tickets already enrolled for live posting via the
// per-ticket toggle. `senders` is always lowercase and de-duplicated
// server-side (pom-2-api settings.service.ts `updateIgnoredSenders`), so the
// client sends whatever the user typed and trusts the server's normalized
// response as the source of truth for what actually got saved.
export interface IgnoredSendersResult {
  senders: string[];
  // Present only once at least one save has ever happened — absent (not
  // null) on a doc that's never been written, same convention as
  // SupportAgentModeResult above.
  updatedBy?: string;
  updatedAt?: string;
}

export interface SubmitVerdictInput {
  verdict: 'up' | 'down';
  note?: string;
  // Targets a specific append-only record (Mongo _id hex string). Omitted
  // -> server falls back to the latest record for the ticket (pre-existing
  // behavior). The Activity drawer always sends the recordId of the row
  // that's actually open, so a verdict never silently lands on a
  // DIFFERENT draft of the same ticket.
  recordId?: string;
}

// ── KB file upload (S3) ────────────────────────────────────────────────────
// kbUpload.service.ts (feature/support-agent-admin@6d1908a). `filename`'s
// extension doubles as `contentType` — mirrors the server's
// KB_UPLOAD_ALLOWED_TYPES / CONTENT_TYPE_MIME exactly.
// NOTE: legacy 'doc' (application/msword) was dropped from this allowlist —
// removed here in lockstep with a parallel pom-2-api fix dropping it from
// kbUpload.service.ts's own CONTENT_TYPE_MIME. Keep the two in sync.
export const KB_UPLOAD_ALLOWED_TYPES = ['pdf', 'docx', 'md', 'txt', 'html'] as const;
export type KbUploadContentType = (typeof KB_UPLOAD_ALLOWED_TYPES)[number];

const KB_UPLOAD_CONTENT_TYPE_MIME: Record<KbUploadContentType, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  md: 'text/markdown',
  txt: 'text/plain',
  html: 'text/html',
};

/** Lowercased file extension, mapped to an allowed upload content type, or `null` if the file type isn't supported. */
export function contentTypeForFilename(filename: string): KbUploadContentType | null {
  const ext = filename.slice(filename.lastIndexOf('.') + 1).toLowerCase();
  return (KB_UPLOAD_ALLOWED_TYPES as readonly string[]).includes(ext) ? (ext as KbUploadContentType) : null;
}

export interface CreateKbUploadUrlInput {
  filename: string;
  contentType: KbUploadContentType;
  // Optional — lets the server reject an oversized file before minting a
  // presigned URL for it. pom-2-api's upload-url validator now accepts this
  // and rejects anything over KB_UPLOAD_MAX_BYTES server-side too (parallel
  // fix). Not required by the schema — omit for older server builds.
  fileSize?: number;
}

export interface UploadUrlResult {
  url: string;
  key: string;
}

// KB upload size cap (25 MB). Mirrors the cap enforced in TWO other places
// by parallel fixes landed alongside this one: pom-2-api's upload-url
// request validation (kbUpload.service.ts / support-agent.validations.ts)
// and the separate text-extraction pipeline. Keep all three in sync if this
// ever changes.
export const KB_UPLOAD_MAX_BYTES = 26_214_400; // 25 MB

/**
 * Returns a human-readable error message if `file` exceeds the KB upload
 * size cap, or `null` if it's within bounds. Checked client-side BEFORE
 * requesting a presigned URL — no point minting one (and no point making
 * the round trip) for a file the server will reject anyway.
 */
export function kbUploadSizeError(file: { size: number }): string | null {
  if (file.size <= KB_UPLOAD_MAX_BYTES) return null;
  const mb = Math.round(file.size / (1024 * 1024));
  return `File is ${mb} MB — the limit is 25 MB`;
}

export interface RegisterUploadInput {
  key: string;
  title: string;
}

export interface SourceUrlResult {
  url: string;
  key: string;
}

// ── KB bulk title lookup ────────────────────────────────────────────────
// POST /kb/titles { ids: [...] } -> [{ id, title }] — replaces the Activity
// drawer's old per-citation GET /kb/:id (each of which pulled the doc's
// full body + version history just to read a title — an N+1 that scaled
// with citation count). Contract per the parallel pom-2-api fix in
// src/services/support-agent/kb.service.ts (feature/support-agent-admin):
// as of this writing that endpoint hadn't landed yet on that branch, so
// this client is built to the contract exactly, following the repo's
// existing `Envelope<T>` response shape — verify against the server
// implementation once it's committed.
export interface KbTitleLookupItem {
  id: string;
  title: string;
}

/**
 * PUTs the raw file bytes to a presigned S3 URL. Deliberately plain
 * `fetch`, NOT `supportAgentHttp` — a presigned URL is pre-authorized by
 * its own signature; sending our bearer token to S3 would do nothing
 * useful and would trip an unnecessary CORS preflight against a foreign
 * host. The Content-Type sent here MUST match the one used to sign the
 * URL (kbUpload.service.ts `CONTENT_TYPE_MIME`) or S3 rejects the PUT.
 */
export async function uploadFileToPresignedUrl(
  url: string,
  file: File | Blob,
  contentType: KbUploadContentType,
): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': KB_UPLOAD_CONTENT_TYPE_MIME[contentType] },
  });
  if (!res.ok) {
    throw new Error(`Upload to storage failed (HTTP ${res.status})`);
  }
}

// ── Persisted chat + manual escalation (Ticket "AI chat escalation v1", 9/9)
// pom-2-api `supportAgentChats` collection / chat.service.ts — the real,
// persisted widget conversation (POST /support-agent/chat et al.), not the
// admin-only unpersisted dry-run tool that used to back the Test
// Conversation tab.
export type ChatStatus = 'ai' | 'escalated' | 'resolved';
export type ChatTurnRole = 'customer' | 'agent' | 'human';

// Same last-resort circuit breaker chat.service.ts enforces
// (support-agent.validations.ts CHAT_HARD_TURN_CAP, 9/11 — the old 30-turn
// cap was cutting off genuine long conversations; abuse is now caught
// earlier server-side by a rate limit and a duplicate-message check
// instead) — client-side so a caller gets an immediate, friendly rejection
// instead of a round trip to find out the payload was too big. In practice
// invisible: no real conversation reaches this.
export const CHAT_MAX_TURNS = 500;
export const CHAT_MAX_BODY_CHARS = 10_000;

// One turn in a persisted chat. `topic`/`confidence`/citations/`costUsd`/
// `urgent`/`urgencyReason`/`category`/`skipped`/`safetyCaveat` are only ever
// set on `role: 'agent'` turns (upstream-passthrough from the dry-run
// Lambda) — an urgent or skipped agent turn's `body` is an empty string
// server-side (see chat.service.ts postMessage), so callers must derive
// display text from these flags, never from `body` alone. `by` is only set
// on `role: 'human'` turns — the POM Master's email.
// ── Chat attachments (Michael, 10/14; pom-2-api #578) ─────────────────────
// Files on an ESCALATED chat, both directions. Same allow-list and caps as
// pom-2-api's support-agent.validations.ts; a file the server would refuse
// is refused here first, before any upload.
export const CHAT_ATTACHMENT_ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;
export type ChatAttachmentType = (typeof CHAT_ATTACHMENT_ALLOWED_TYPES)[number];
export const CHAT_ATTACHMENT_MAX_BYTES = 10_485_760; // 10 MiB
export const CHAT_ATTACHMENTS_MAX_PER_MESSAGE = 5;

// A file on a turn as the server returns it: the stored fields plus a fresh
// signed `url` (15 min) minted on every read. `url` is absent only when the
// server could not sign it; `urlExpiresAt` says when to refetch the chat
// before opening it.
export interface ChatAttachment {
  key: string;
  name: string;
  type: string;
  size: number;
  url?: string;
  urlExpiresAt?: string;
}

// What a message sends per file: exactly the four fields the upload link returned.
export interface ChatAttachmentInput {
  key: string;
  name: string;
  type: string;
  size: number;
}

// POST /chat/:id/attachments: a 5-minute presigned PUT. The client PUTs the
// raw bytes to `uploadUrl` with exactly `headers`, then sends the message
// naming `key`.
export interface ChatAttachmentUploadLink extends ChatAttachmentInput {
  uploadUrl: string;
  headers: Record<string, string>;
  expiresAt: string;
}

export function attachmentUrlExpired(attachment: Pick<ChatAttachment, 'url' | 'urlExpiresAt'>, now = Date.now()): boolean {
  if (!attachment.url) return true;
  if (!attachment.urlExpiresAt) return false;
  const at = new Date(attachment.urlExpiresAt).getTime();
  return Number.isFinite(at) && at <= now;
}

/**
 * PUTs a chat attachment to its presigned url. XMLHttpRequest rather than
 * fetch so the composer can show upload progress; plain, not
 * `supportAgentHttp`, for the same reason as uploadFileToPresignedUrl above.
 * The headers MUST be sent exactly as returned: the url signs the byte size
 * and S3 refuses any other body.
 */
export function uploadChatAttachment(
  link: Pick<ChatAttachmentUploadLink, 'uploadUrl' | 'headers'>,
  file: File | Blob,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', link.uploadUrl);
    Object.entries(link.headers ?? {}).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(Math.min(1, e.loaded / e.total));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve();
      } else {
        reject(new Error(`Upload to storage failed (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload to storage failed'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.send(file);
  });
}

export interface ChatTurn {
  role: ChatTurnRole;
  // Empty on a files-only turn.
  body: string;
  at: string;
  by?: string;
  // Files sent with this turn ('customer' and 'human' turns, escalated chats
  // only). Absent on turns without files.
  attachments?: ChatAttachment[];
  topic?: string;
  confidence?: number;
  citedChunkIds?: string[];
  citedDocIds?: string[];
  // 'agent' turns only (pom-2-api stores them verbatim from the bot, 9/11):
  // the articles the assistant was given for this reply, and the ones this
  // turn's own search found. `citedDocIds` are always among the given ones.
  // Absent on turns written before the fields existed.
  suppliedDocIds?: string[];
  retrievedDocIds?: string[];
  // 'agent' turns only (Michael, 10/28): the query this reply's own KB search
  // ran, or null when it ran none, and who decided it — the bot's search
  // planner or its fallback word rules. Absent on older, urgent/skipped and
  // chunks-mode turns.
  searchQuery?: string | null;
  searchDecidedBy?: 'planner' | 'word-lists';
  // 'agent' turns only (Michael, 10/28): the candidate articles this reply's
  // wide search found, best first, and whether its retrievedDocIds are the
  // bot's article picker's choice or the top results. Absent when no pick
  // step ran.
  searchCandidateDocIds?: string[];
  searchPickedBy?: 'picker' | 'top-results';
  contextMode?: 'chunks' | 'articles';
  costUsd?: number;
  urgent?: boolean;
  urgencyReason?: string;
  category?: SupportAgentChatTurnCategory;
  skipped?: boolean;
  safetyCaveat?: boolean;
}

// The raw persisted doc, as returned by POST /chat, GET /chat/:id,
// POST /chat/:id/escalate|reply|resolve — `_id` (not `chatId`; that renaming
// only happens in ChatListItem below, from the LIST endpoint).
// Mobile AI chat (pom-2-api #554): which surface created the chat — absent
// on chats from before the field, all of which are web chats. App users sign
// in by phone and may have no email, so their chats carry the phone number.
export type ChatChannel = 'web' | 'app';

export interface ChatDoc {
  _id: string;
  customerId?: string;
  userId: string;
  userEmail?: string;
  channel?: ChatChannel;
  // Which deployment made the chat ('prod', 'dev', 'qa'); none on chats
  // from before 9/29, which count as prod's (pom-2-api #621).
  stage?: string;
  userPhoneNumber?: string;
  // The user's name: app chats from their profile, web chats (pom-2-api
  // #604) from the Safety Hub login. Absent on older web chats.
  userName?: string;
  // POM-Master reads only (pom-2-api #604): which rep resolved it (email),
  // support's label, and the account's organization name.
  resolvedByStaff?: string;
  review?: ChatReview;
  orgName?: string;
  status: ChatStatus;
  turns: ChatTurn[];
  escalation?: { at: string; byUserId: string; reason?: string };
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  // Who/what resolved the chat (Interrogator finding, 2026-09-11 round 2 —
  // stamped by pom-2-api's resolve()/reArchive() on every resolve path, but
  // previously absent from this type entirely, so the Chats drawer couldn't
  // distinguish a customer self-resolve from a staff resolve or the
  // 15-minute inactivity sweep even though the data was already there).
  // Who closed the chat. 'ai' here means the assistant closed it after the
  // customer agreed — unrelated to `status: 'ai'` (a chat still with the bot).
  resolvedBy?: 'customer' | 'staff' | 'inactivity' | 'ai';
  zendeskTicketId?: string | null;
  // Set (alongside `status: 'resolved'`) for the instant between claiming an
  // escalated chat and the archive-to-Zendesk call finishing, and restored
  // if that call fails or comes back incomplete — never set for an ai-only
  // resolve, since there's nothing to archive (pom-2-api chat.service.ts
  // `resolve()`/`reArchive()`). A POM Master can re-run the archive attempt
  // via `POST /chat/:id/archive` (see `archiveChat` below) while this is
  // true. Absent (not false) whenever it doesn't apply.
  archivePending?: boolean;
  // Set true when the archive call's transcript upload could not be
  // included atomically with the created ticket — the ticket exists but
  // should be verified directly in Zendesk. Absent (not false) whenever it
  // doesn't apply, same convention as `archivePending`.
  archiveIncomplete?: boolean;
  // Server-persisted "last viewed" (Carlos, 9/11) — stamped by
  // POST /chat/:id/seen whenever the widget's panel is open. The widget
  // derives its unread count/badge by comparing turn/escalation/resolvedAt
  // timestamps against this, instead of tracking seen-state client-side
  // (which forgot everything on a page refresh). Absent on a chat the
  // owner has never opened, or one that predates this field.
  lastSeenAt?: string;
  // The mirror from our side (Michael, 10/15) — stamped by POST
  // /chat/:id/staff-seen (the Chats inbox, while a person has the chat open
  // in a visible tab) and by a POM Master reply, server-side. The app shows
  // the customer two green checks on their messages at or before it. The
  // inbox doesn't display it. Absent on a chat support has never looked at,
  // or one that predates this field.
  staffSeenAt?: string;
  // Feedback card (Michael, 10/20; pom-2-api #597). `feedbackEligible` is
  // the server's "show the card now" decision (resolved by the bot, the
  // customer or support within 7 days, no urgent turn, no feedback yet) —
  // the widget renders the card on it and never re-derives the rule.
  // Absent from a server that predates it, which reads as false.
  feedbackEligible?: boolean;
  // The customer's answers once sent (POST /chat/:id/feedback). The widget
  // renders the locked card from this, the inbox drawer shows it.
  feedback?: ChatFeedback;
}

export type ChatFeedbackResolution = 'fully' | 'partially' | 'not_at_all';
export interface ChatFeedback {
  resolution: ChatFeedbackResolution;
  // 1–5, always asked.
  assistantRating: number;
  // 1–5, only on a chat a person joined (`escalation` set).
  supportRating?: number;
  comment?: string;
  at: string;
}
export type ChatFeedbackInput = Omit<ChatFeedback, 'at'>;
export const CHAT_FEEDBACK_COMMENT_MAX_CHARS = 1000;
export const CHAT_FEEDBACK_RESOLUTION_LABEL: Record<ChatFeedbackResolution, string> = {
  fully: 'Fully',
  partially: 'Partially',
  not_at_all: 'Not at all',
};

// Support's label on a closed chat (pom-2-api #604): an escalated chat
// says why it needed a person; one the assistant closed alone and the
// customer rated "Not at all" says what went wrong. Labels for the eval
// pipeline; the customer never sees them.
export type ChatReviewKind = 'escalated' | 'rated';
export const CHAT_REVIEW_REASONS = {
  escalated: [
    { key: 'wrong_answer', text: 'Wrong or incomplete answer', hint: 'The assistant answered, but not correctly.' },
    { key: 'not_in_kb', text: 'Not in the knowledge base', hint: 'No article covers this yet.' },
    { key: 'account_action', text: 'Needs an account action', hint: 'Only a person can do it.' },
    { key: 'wanted_person', text: 'Customer just wanted a person', hint: 'The assistant was on track.' },
    { key: 'urgent', text: 'Urgent or safety', hint: 'A possible emergency.' },
    { key: 'didnt_need_person', text: "Didn't need a person", hint: 'The assistant could have answered it.' },
  ],
  rated: [
    { key: 'wrong_answer', text: 'Wrong or incomplete answer', hint: 'The assistant answered, but not correctly.' },
    { key: 'not_in_kb', text: 'Not in the knowledge base', hint: 'No article covers this yet.' },
    { key: 'should_have_handed_off', text: 'Should have handed off', hint: 'Only a person could have helped.' },
    { key: 'answer_was_right', text: 'The answer was right', hint: 'The customer was unhappy anyway.' },
  ],
} as const;
// The reasons that ask "Which article has the right answer?".
export const CHAT_REVIEW_ARTICLE_REASONS: readonly string[] = ['wrong_answer', 'didnt_need_person'];
export const CHAT_REVIEW_NOTE_MAX_CHARS = 1000;
export interface ChatReview {
  kind: ChatReviewKind;
  reason: string;
  articleId?: string;
  articleWrong?: boolean;
  note?: string;
  // The POM Master who labelled it (email), and when.
  by: string;
  at: string;
}
export interface ChatReviewInput {
  reason: string;
  articleId?: string;
  articleWrong?: boolean;
  note?: string;
}
/**
 * The signed-in Safety Hub user's email: the POM Master working the Chats
 * inbox. Stored chat fields name a rep by email (a reply's `by`, the
 * label's `by`, `resolvedByStaff`, rep typing's `by`), so this tells "you"
 * from another rep.
 */
export function signedInEmail(): string | undefined {
  const email = (store.getState() as any)?.auth?.authUser?.email;
  return typeof email === 'string' && email ? email : undefined;
}

/** The words for a stored reason key, from either set. */
export function chatReviewReasonText(reason: string): string {
  const all = [...CHAT_REVIEW_REASONS.escalated, ...CHAT_REVIEW_REASONS.rated];
  return all.find((r) => r.key === reason)?.text ?? reason;
}

// One row in the POM Master inbox (GET /support-agent/chats).
export interface ChatListItem {
  chatId: string;
  customerId?: string;
  userId: string;
  userEmail?: string;
  channel?: ChatChannel;
  // Which deployment made the chat ('prod', 'dev', 'qa'); none on chats
  // from before 9/29, which count as prod's (pom-2-api #621).
  stage?: string;
  userPhoneNumber?: string;
  // The user's name: app chats from their profile, web chats (pom-2-api
  // #604) from the Safety Hub login. Absent on older web chats.
  userName?: string;
  status: ChatStatus;
  turnCount: number;
  lastTurnAt: string;
  createdAt: string;
  resolvedAt?: string;
  zendeskTicketId?: string | null;
  // Files across all turns (pom-2-api #578). Absent from a server that
  // predates it.
  attachmentCount?: number;
  // The feedback card's answers (pom-2-api #597), once the customer sent
  // them. Absent until then, or from a server that predates it.
  feedback?: ChatFeedback;
  // pom-2-api #604 (absent from a server that predates it): whether a
  // person was ever asked for, which label question it gets (absent: none),
  // the label once saved, the customer's first message (140 characters),
  // and who closed it — `resolvedByStaff` is the rep's email.
  escalated?: boolean;
  reviewKind?: ChatReviewKind;
  review?: ChatReview;
  firstMessage?: string;
  resolvedBy?: ChatDoc['resolvedBy'];
  resolvedByStaff?: string;
}

export interface ChatListResult {
  items: ChatListItem[];
  total: number;
  page: number;
  limit: number;
}

export const supportAgentApi = {
  // ── Knowledge base ───────────────────────────────────────────────────
  async listKbDocs(params: ListKbDocsParams = {}): Promise<KbDocListResult> {
    const r = await supportAgentHttp.get<Envelope<KbDocListResult>>('/support-agent/kb', { params });
    return r.data.result;
  },

  async getKbDoc(id: string): Promise<KbDocDetail> {
    const r = await supportAgentHttp.get<Envelope<KbDocDetail>>(`/support-agent/kb/${id}`);
    return r.data.result;
  },

  // Full snapshot (incl. body) for one version, by index into the doc's
  // `versions` array (0 = most recent prior version). 404s as
  // KB_DOC_NOT_FOUND / KB_DOC_VERSION_NOT_FOUND.
  async getKbDocVersion(id: string, index: number): Promise<KbDocVersionDetail> {
    const r = await supportAgentHttp.get<Envelope<KbDocVersionDetail>>(
      `/support-agent/kb/${id}/versions/${index}`,
    );
    return r.data.result;
  },

  async createKbDoc(input: CreateKbDocInput): Promise<KbDocDetail> {
    const r = await supportAgentHttp.post<Envelope<KbDocDetail>>('/support-agent/kb', input);
    return r.data.result;
  },

  async updateKbDoc(id: string, input: UpdateKbDocInput): Promise<KbDocDetail> {
    const r = await supportAgentHttp.put<Envelope<KbDocDetail>>(`/support-agent/kb/${id}`, input);
    return r.data.result;
  },

  // Soft delete — server flips status to 'archived' and clears chunks.
  async archiveKbDoc(id: string): Promise<void> {
    await supportAgentHttp.delete<Envelope<boolean>>(`/support-agent/kb/${id}`);
  },

  // Step 1 of upload: get a presigned S3 PUT url + object key.
  async createKbUploadUrl(input: CreateKbUploadUrlInput): Promise<UploadUrlResult> {
    const r = await supportAgentHttp.post<Envelope<UploadUrlResult>>('/support-agent/kb/upload-url', input);
    return r.data.result;
  },

  // Step 3 of upload (step 2 is the caller PUTting bytes to the presigned
  // url via `uploadFileToPresignedUrl`, not through this client): create
  // the draft kbDoc pointing at the now-uploaded object. Body is a
  // placeholder ("*(File uploaded — text extraction pending…") until the
  // separate extraction pipeline (pomsafe-services) fills it in.
  async registerKbUpload(input: RegisterUploadInput): Promise<KbDocDetail> {
    const r = await supportAgentHttp.post<Envelope<KbDocDetail>>('/support-agent/kb/register-upload', input);
    return r.data.result;
  },

  // Presigned GET for the original uploaded file. 404s as SOURCE_NOT_FOUND
  // when the doc has no `sourceFileKey`.
  async getKbSourceUrl(id: string): Promise<SourceUrlResult> {
    const r = await supportAgentHttp.get<Envelope<SourceUrlResult>>(`/support-agent/kb/${id}/source-url`);
    return r.data.result;
  },

  // ── KB conflict check (Michael, 9/19) — pom-2-api proxies the
  //    pomsafe-services engine; the UI polls until a terminal state. ──────

  // 202 + checkId. The body is the editor's UNSAVED draft on purpose: saving
  // a published article as a draft takes it offline (kb.service.ts clears its
  // chunks), so the check never goes through a save.
  async createKbConflictCheck(input: CreateKbConflictCheckInput): Promise<CreateKbConflictCheckResult> {
    const r = await supportAgentHttp.post<Envelope<CreateKbConflictCheckResult>>('/support-agent/kb/conflict-checks', input);
    return r.data.result;
  },

  async getKbConflictCheck(checkId: string): Promise<KbConflictCheck> {
    const r = await supportAgentHttp.get<Envelope<KbConflictCheck>>(`/support-agent/kb/conflict-checks/${checkId}`);
    return r.data.result;
  },

  // A doc's recent checks, newest first (summaries only).
  async listKbConflictChecks(docId: string, limit = 5): Promise<KbConflictCheckSummary[]> {
    const r = await supportAgentHttp.get<Envelope<KbConflictCheckSummary[]>>(`/support-agent/kb/${docId}/conflict-checks`, {
      params: { limit },
    });
    return r.data.result;
  },

  // The POM Master's decision on one finding. dismiss / accept_risk require a reason (server-enforced).
  async setKbConflictFindingDisposition(
    checkId: string,
    findingId: string,
    input: SetKbConflictFindingDispositionInput,
  ): Promise<{ checkId: string; findingId: string; disposition: KbConflictFindingDisposition }> {
    const r = await supportAgentHttp.post<Envelope<{ checkId: string; findingId: string; disposition: KbConflictFindingDisposition }>>(
      `/support-agent/kb/conflict-checks/${checkId}/findings/${findingId}/disposition`,
      input,
    );
    return r.data.result;
  },

  // Bulk title lookup for citation display — one round trip instead of one
  // GET per cited doc. `ids` may include ids for docs that no longer exist
  // (archived/deleted); callers should fall back to the raw id for any
  // missing from the response.
  async getKbDocTitles(ids: string[]): Promise<KbTitleLookupItem[]> {
    const r = await supportAgentHttp.post<Envelope<KbTitleLookupItem[]>>('/support-agent/kb/titles', { ids });
    return r.data.result;
  },

  // ── Instructions ─────────────────────────────────────────────────────
  async getInstructions(): Promise<InstructionsResult> {
    const r = await supportAgentHttp.get<Envelope<InstructionsResult>>('/support-agent/instructions');
    return r.data.result;
  },

  async updateInstructions(text: string): Promise<InstructionsResult> {
    const r = await supportAgentHttp.put<Envelope<InstructionsResult>>('/support-agent/instructions', { text });
    return r.data.result;
  },

  // ── Activity ─────────────────────────────────────────────────────────
  async listActivity(params: { page?: number; limit?: number } = {}): Promise<ActivityListResult> {
    const r = await supportAgentHttp.get<Envelope<ActivityListResult>>('/support-agent/activity', { params });
    return r.data.result;
  },

  async getActivityByTicketId(zendeskTicketId: number | string): Promise<SupportAgentTicketRecord> {
    const r = await supportAgentHttp.get<Envelope<SupportAgentTicketRecord>>(
      `/support-agent/activity/${zendeskTicketId}`,
    );
    return r.data.result;
  },

  async submitVerdict(
    zendeskTicketId: number | string,
    input: SubmitVerdictInput,
  ): Promise<SupportAgentTicketRecord> {
    const r = await supportAgentHttp.post<Envelope<SupportAgentTicketRecord>>(
      `/support-agent/activity/${zendeskTicketId}/verdict`,
      input,
    );
    return r.data.result;
  },

  // Full Zendesk comment thread for a ticket — the Activity drawer's
  // "Conversation" section. Works for both observed and drafted records
  // (both carry a zendeskTicketId).
  async getActivityThread(zendeskTicketId: number | string): Promise<ActivityThreadResult> {
    const r = await supportAgentHttp.get<Envelope<ActivityThreadResult>>(
      `/support-agent/activity/${zendeskTicketId}/thread`,
    );
    return r.data.result;
  },

  // ALL append-only records for a ticket, newest first — backs the Activity
  // drawer's "Bot activity" timeline now that the list itself is one row
  // per ticket (pom-2-api activity.service.ts `listRecordsForTicket`, 8/12
  // rework). Empty array for a ticket with no records — not a 404.
  async getActivityRecords(zendeskTicketId: number | string): Promise<SupportAgentTicketRecord[]> {
    const r = await supportAgentHttp.get<Envelope<SupportAgentTicketRecord[]>>(
      `/support-agent/activity/${zendeskTicketId}/records`,
    );
    return r.data.result;
  },

  // Per-ticket posting toggle — see UpdatePostingResult above. Enabling may
  // post the ticket's current draft immediately (server-side); the caller
  // (activity-panel.tsx) reflects `immediatePost` in a snackbar.
  async updatePosting(zendeskTicketId: number | string, enabled: boolean): Promise<UpdatePostingResult> {
    const r = await supportAgentHttp.put<Envelope<UpdatePostingResult>>(
      `/support-agent/activity/${zendeskTicketId}/posting`,
      { enabled },
    );
    return r.data.result;
  },

  // ── Settings ─────────────────────────────────────────────────────────
  async getAgentMode(): Promise<SupportAgentModeResult> {
    const r = await supportAgentHttp.get<Envelope<SupportAgentModeResult>>('/support-agent/settings/mode');
    return r.data.result;
  },

  async updateAgentMode(mode: SupportAgentGlobalMode): Promise<SupportAgentModeResult> {
    const r = await supportAgentHttp.put<Envelope<SupportAgentModeResult>>('/support-agent/settings/mode', {
      mode,
    });
    return r.data.result;
  },

  // Idle-chat auto-resolve — see SupportAgentChatAutoResolveResult above.
  async getChatAutoResolve(): Promise<SupportAgentChatAutoResolveResult> {
    const r = await supportAgentHttp.get<Envelope<SupportAgentChatAutoResolveResult>>(
      '/support-agent/settings/chat-auto-resolve',
    );
    return r.data.result;
  },

  async updateChatAutoResolve(hours: number): Promise<SupportAgentChatAutoResolveResult> {
    const r = await supportAgentHttp.put<Envelope<SupportAgentChatAutoResolveResult>>(
      '/support-agent/settings/chat-auto-resolve',
      { hours },
    );
    return r.data.result;
  },

  // Ignored senders list — see IgnoredSendersResult above.
  async getIgnoredSenders(): Promise<IgnoredSendersResult> {
    const r = await supportAgentHttp.get<Envelope<IgnoredSendersResult>>('/support-agent/settings/ignored-senders');
    return r.data.result;
  },

  // `senders` is sent as-typed (whatever case/order the caller built up
  // client-side) — the server normalizes (lowercase + dedupe) and the
  // response is the source of truth for what actually got saved.
  async updateIgnoredSenders(senders: string[]): Promise<IgnoredSendersResult> {
    const r = await supportAgentHttp.put<Envelope<IgnoredSendersResult>>('/support-agent/settings/ignored-senders', {
      senders,
    });
    return r.data.result;
  },

  // ── Persisted chat + manual escalation ──────────────────────────────
  // Create (no chatId) or continue (chatId) the caller's own chat. The AI
  // only replies while the chat's status is 'ai' — see ChatDoc above.
  // `body` is optional only alongside `attachments` (escalated chats only).
  async postChatMessage(input: {
    chatId?: string;
    subject?: string;
    body?: string;
    attachments?: ChatAttachmentInput[];
  }): Promise<ChatDoc> {
    const r = await supportAgentHttp.post<Envelope<ChatDoc>>('/support-agent/chat', input);
    return r.data.result;
  },

  // Owner or POM Master — a presigned PUT for one file on an escalated chat
  // (409 while the AI still has it or once resolved; 400 for a type or size
  // the server does not take). Upload with uploadChatAttachment, then name
  // `key` in postChatMessage / replyToChat.
  async createChatAttachmentUploadLink(
    chatId: string,
    input: { name: string; type: ChatAttachmentType; size: number },
  ): Promise<ChatAttachmentUploadLink> {
    const r = await supportAgentHttp.post<Envelope<ChatAttachmentUploadLink>>(
      `/support-agent/chat/${chatId}/attachments`,
      input,
    );
    return r.data.result;
  },

  // The caller's own most recent not-resolved chat, or null — what a page
  // refresh restores from. The server answers 204 (no body) when there's
  // none; axios's default validateStatus already treats 204 as success (it
  // falls in the 2xx range), so no override is needed here, just a status
  // check before touching `r.data`.
  async getCurrentChat(): Promise<ChatDoc | null> {
    const r = await supportAgentHttp.get<Envelope<ChatDoc>>('/support-agent/chat/current');
    if (r.status === 204) return null;
    return r.data.result;
  },

  // `since` (ISO string) narrows `turns` to strictly-after that timestamp —
  // the polling cursor. Omit for the full transcript.
  async getChat(chatId: string, since?: string): Promise<ChatDoc> {
    const r = await supportAgentHttp.get<Envelope<ChatDoc>>(`/support-agent/chat/${chatId}`, {
      params: since ? { since } : undefined,
    });
    return r.data.result;
  },

  // Manual escalation only — no AI-triggered escalation in this version.
  async escalateChat(chatId: string, reason?: string): Promise<ChatDoc> {
    const r = await supportAgentHttp.post<Envelope<ChatDoc>>(`/support-agent/chat/${chatId}/escalate`, {
      reason,
    });
    return r.data.result;
  },

  // POM-Master-only — every persisted chat (every stage's; prod's inbox
  // only prod's), including practice chats from this same widget.
  // pom-2-api #604: `userId` (one person's chats), `search` (name, email or
  // phone; 2+ characters) and `needsLabel` (closed chats waiting for
  // support's label). #621: `prodOnly` (the inbox's Hide test chats).
  async listChats(
    params: {
      status?: ChatStatus;
      feedbackResolution?: ChatFeedbackResolution;
      userId?: string;
      search?: string;
      needsLabel?: boolean;
      prodOnly?: boolean;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<ChatListResult> {
    const r = await supportAgentHttp.get<Envelope<ChatListResult>>('/support-agent/chats', { params });
    return r.data.result;
  },

  // POM-Master-only — support's label on a closed chat (pom-2-api #604).
  // Saving again replaces it. 409 when the chat can't be labelled, 400 for
  // a reason from the other set or an article with a reason that doesn't
  // ask for one.
  async reviewChat(chatId: string, input: ChatReviewInput): Promise<ChatDoc> {
    const r = await supportAgentHttp.post<Envelope<ChatDoc>>(`/support-agent/chat/${chatId}/review`, input);
    return r.data.result;
  },

  // POM-Master-only — "the agent is typing" for the chat's owner (live web
  // chat, Michael 10/8): the server stamps it on the chat and websocket-server
  // relays it as `chat_typing`. 204; 409 if the chat isn't escalated. Sent at
  // most every few seconds while typing and with `typing: false` on stop; a
  // reply clears it server-side on its own.
  async setChatTyping(chatId: string, typing: boolean): Promise<void> {
    await supportAgentHttp.post(`/support-agent/chat/${chatId}/typing`, { typing });
  },

  // POM-Master-only — "seen by support" (Michael, 10/15), the mirror of the
  // customer's `markChatSeen` below: stamps `staffSeenAt` on the chat, which
  // the app turns into two green checks on the customer's messages up to it.
  // 204; 404 for an unknown chat. Sent while a person actually has the chat
  // open in the inbox (chats-panel.tsx); a reply stamps it server-side on
  // its own, so it is never sent on reply. Fire-and-forget, like typing.
  async markChatStaffSeen(chatId: string): Promise<void> {
    await supportAgentHttp.post(`/support-agent/chat/${chatId}/staff-seen`, {});
  },

  // The chat's owner — "Customer is typing" for the Chats inbox (Michael,
  // 10/10). Only accepted once a chat has been escalated.
  async setCustomerTyping(chatId: string, typing: boolean): Promise<void> {
    await supportAgentHttp.post(`/support-agent/chat/${chatId}/customer-typing`, { typing });
  },

  // POM-Master-only — only accepted once a chat has been escalated. A plain
  // string is the text-only reply; the object form adds files (`body` then
  // optional).
  async replyToChat(
    chatId: string,
    input: string | { body?: string; attachments?: ChatAttachmentInput[] },
  ): Promise<ChatDoc> {
    const payload = typeof input === 'string' ? { body: input } : input;
    const r = await supportAgentHttp.post<Envelope<ChatDoc>>(`/support-agent/chat/${chatId}/reply`, payload);
    return r.data.result;
  },

  // POM-Master-only — archives the transcript as a Zendesk ticket
  // server-side (best-effort; `zendeskTicketId` may come back null).
  async resolveChat(chatId: string): Promise<ChatDoc> {
    const r = await supportAgentHttp.post<Envelope<ChatDoc>>(`/support-agent/chat/${chatId}/resolve`, {});
    return r.data.result;
  },

  /**
   * Feedback card (Michael, 10/20; pom-2-api #597): the owner's answers for
   * their own resolved chat, once. Answers the chat with `feedback` set —
   * the widget renders the locked card from it. 409 once feedback was
   * already sent or the chat is no longer eligible; 400 for a bad body
   * (`supportRating` is required exactly when a person joined).
   */
  async submitChatFeedback(chatId: string, input: ChatFeedbackInput): Promise<ChatDoc> {
    const r = await supportAgentHttp.post<Envelope<ChatDoc>>(`/support-agent/chat/${chatId}/feedback`, input);
    return r.data.result;
  },

  // POM-Master-only — re-runs a failed/timed-out archive attempt for a chat
  // whose resolve left `archivePending: true`. The server rejects this
  // (SUPPORT_AGENT_CHAT_ARCHIVE_NOT_PENDING) when that flag isn't set.
  async archiveChat(chatId: string): Promise<ChatDoc> {
    const r = await supportAgentHttp.post<Envelope<ChatDoc>>(`/support-agent/chat/${chatId}/archive`, {});
    return r.data.result;
  },

  // Owner-only (Carlos, 9/11) — stamps `lastSeenAt` to now. The widget calls
  // this whenever its panel opens; never called from the POM Master Chats
  // inbox, which would incorrectly mark the customer's own unread state as
  // seen on their behalf.
  async markChatSeen(chatId: string): Promise<ChatDoc> {
    const r = await supportAgentHttp.post<Envelope<ChatDoc>>(`/support-agent/chat/${chatId}/seen`, {});
    return r.data.result;
  },
};
